import React, { useState } from 'react';
import { Check, Leaf, Drumstick, Fish, Egg, Wheat, AlertTriangle, Loader2 } from 'lucide-react';

const DIETARY_OPTIONS = [
  { id: 'vegetarian',   label: 'Vegetarian',     Icon: Leaf,      sub: 'No meat or fish' },
  { id: 'vegan',        label: 'Vegan',          Icon: Leaf,      sub: 'Fully plant-based' },
  { id: 'veg_nonveg',   label: 'Veg + Non-Veg',  Icon: Drumstick, sub: 'Open to everything' },
  { id: 'pescatarian',  label: 'Pescatarian',    Icon: Fish,      sub: 'Fish but no other meat' },
  { id: 'eggetarian',   label: 'Eggetarian',     Icon: Egg,       sub: 'Veg + eggs' },
  { id: 'jain',         label: 'Jain',           Icon: Wheat,     sub: 'No root vegetables' },
];

const CUISINE_OPTIONS = [
  { id: 'indian',        label: 'Indian',         emoji: '🇮🇳' },
  { id: 'continental',   label: 'Continental',    emoji: '🍽️' },
  { id: 'mediterranean', label: 'Mediterranean',  emoji: '🫒' },
  { id: 'asian',         label: 'Asian',          emoji: '🥢' },
  { id: 'italian',       label: 'Italian',        emoji: '🍝' },
  { id: 'mexican',       label: 'Mexican',        emoji: '🌮' },
  { id: 'middle_eastern',label: 'Middle Eastern', emoji: '🥙' },
  { id: 'japanese',      label: 'Japanese',       emoji: '🍣' },
  { id: 'chinese',       label: 'Chinese',        emoji: '🥟' },
  { id: 'thai',          label: 'Thai',           emoji: '🍜' },
  { id: 'american',      label: 'American',       emoji: '🍔' },
  { id: 'south_indian',  label: 'South Indian',   emoji: '🥥' },
];

const ALLERGEN_OPTIONS = [
  { id: 'peanuts',     label: 'Peanuts' },
  { id: 'tree_nuts',   label: 'Tree Nuts' },
  { id: 'dairy',       label: 'Dairy' },
  { id: 'gluten',      label: 'Gluten / Wheat' },
  { id: 'shellfish',   label: 'Shellfish' },
  { id: 'eggs',        label: 'Eggs' },
  { id: 'soy',         label: 'Soy' },
  { id: 'sesame',      label: 'Sesame' },
  { id: 'mushrooms',   label: 'Mushrooms' },
  { id: 'onion_garlic',label: 'Onion / Garlic' },
];

export { DIETARY_OPTIONS, CUISINE_OPTIONS, ALLERGEN_OPTIONS };

export default function MealPreferencesForm({
  initial = {},
  onSubmit,
  submitting = false,
  submitLabel = 'Save & Continue',
  compact = false,
}) {
  const [step, setStep] = useState(1);
  const [dietary, setDietary] = useState(initial.dietary || '');
  const [cuisines, setCuisines] = useState(initial.cuisines || []);
  const [allergens, setAllergens] = useState(initial.allergens || []);
  const [error, setError] = useState('');

  const toggle = (list, setList, id) => {
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && !dietary) { setError('Please pick a dietary preference'); return; }
    if (step === 2 && cuisines.length === 0) { setError('Please pick at least one cuisine'); return; }
    if (step < 3) { setStep(step + 1); return; }
    onSubmit({ dietary, cuisines, allergens });
  };

  const totalSteps = 3;

  return (
    <div className="w-full" data-testid="meal-preferences-form">
      {/* Progress bar */}
      {!compact && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
              Step {step} of {totalSteps}
            </span>
            <span className="text-xs" style={{ color: 'var(--muted-fg)' }}>
              {step === 1 && 'Dietary preference'}
              {step === 2 && 'Preferred cuisines'}
              {step === 3 && 'Allergens & avoid'}
            </span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(step / totalSteps) * 100}%`, background: 'var(--primary)' }} />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="animate-fade-in">
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            What do you eat?
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted-fg)' }}>
            We'll tailor your meal suggestions to fit your plate.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DIETARY_OPTIONS.map(opt => {
              const selected = dietary === opt.id;
              const Icon = opt.Icon;
              return (
                <button key={opt.id} onClick={() => setDietary(opt.id)}
                  className="p-4 rounded-lg border text-left flex items-center gap-3 transition-all hover:-translate-y-0.5"
                  style={{
                    background: selected ? 'var(--primary)' + '12' : 'var(--surface)',
                    borderColor: selected ? 'var(--primary)' : 'var(--border)',
                    boxShadow: selected ? '0 4px 12px rgba(135,155,125,0.15)' : 'none',
                  }}
                  data-testid={`dietary-${opt.id}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: selected ? 'var(--primary)' : 'var(--muted)', color: selected ? 'var(--primary-fg)' : 'var(--muted-fg)' }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm" style={{ color: 'var(--text)', fontFamily: 'Manrope' }}>{opt.label}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{opt.sub}</p>
                  </div>
                  {selected && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--primary)' }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="animate-fade-in">
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Which cuisines excite you?
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted-fg)' }}>
            Pick as many as you love — we'll rotate meals from these.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {CUISINE_OPTIONS.map(opt => {
              const selected = cuisines.includes(opt.id);
              return (
                <button key={opt.id} onClick={() => toggle(cuisines, setCuisines, opt.id)}
                  className="p-3 rounded-lg border text-left flex items-center gap-2.5 transition-all hover:-translate-y-0.5"
                  style={{
                    background: selected ? 'var(--primary)' + '12' : 'var(--surface)',
                    borderColor: selected ? 'var(--primary)' : 'var(--border)',
                  }}
                  data-testid={`cuisine-${opt.id}`}>
                  <span className="text-xl">{opt.emoji}</span>
                  <span className="text-sm font-medium flex-1" style={{ color: 'var(--text)' }}>{opt.label}</span>
                  {selected && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--primary)' }} />}
                </button>
              );
            })}
          </div>
          <p className="text-xs mt-4" style={{ color: 'var(--muted-fg)' }}>{cuisines.length} selected</p>
        </div>
      )}

      {step === 3 && (
        <div className="animate-fade-in">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
              Anything to avoid?
            </h2>
          </div>
          <p className="text-sm mb-6" style={{ color: 'var(--muted-fg)' }}>
            We'll warn you when a suggested meal contains these.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {ALLERGEN_OPTIONS.map(opt => {
              const selected = allergens.includes(opt.id);
              return (
                <button key={opt.id} onClick={() => toggle(allergens, setAllergens, opt.id)}
                  className="p-3 rounded-lg border text-left flex items-center gap-2 transition-all hover:-translate-y-0.5"
                  style={{
                    background: selected ? 'var(--accent)' + '12' : 'var(--surface)',
                    borderColor: selected ? 'var(--accent)' : 'var(--border)',
                  }}
                  data-testid={`allergen-${opt.id}`}>
                  <span className="text-sm font-medium flex-1" style={{ color: 'var(--text)' }}>{opt.label}</span>
                  {selected && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--accent)' }} />}
                </button>
              );
            })}
          </div>
          <p className="text-xs mt-4" style={{ color: 'var(--muted-fg)' }}>
            {allergens.length === 0 ? 'No restrictions — bring it all on!' : `${allergens.length} selected`}
          </p>
        </div>
      )}

      {error && (
        <p className="mt-4 text-sm" style={{ color: '#DC2626' }} data-testid="prefs-error">{error}</p>
      )}

      <div className="mt-8 flex gap-3">
        {step > 1 && (
          <button onClick={() => setStep(step - 1)} data-testid="prefs-back"
            className="px-5 py-3 rounded-lg font-medium text-sm transition-all hover:opacity-80"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
            Back
          </button>
        )}
        <button onClick={handleNext} disabled={submitting} data-testid="prefs-next"
          className="flex-1 py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {step < totalSteps ? 'Continue' : submitLabel}
        </button>
      </div>
    </div>
  );
}
